import { useState } from "react";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import type { UserAssetRecord } from "@apis/http/api";
import { TestId } from "@shared/testIds";
import {
  GENERATED_CARD_HEIGHT,
  GENERATED_COVER_HEIGHT,
  LIBRARY_CARD_WIDTH,
  UPLOADED_CARD_HEIGHT,
  UPLOADED_CARD_MEDIA_HEIGHT,
  type PostVideoLibrarySubTab,
} from "./constants";
import {
  getPostVideoCoverUrl,
  getPostVideoProductImage,
  getPostVideoProductTitle,
  getPostVideoVideoUrl,
} from "./postVideoLibraryAsset";
import { isNotNil } from "@kalodata/tools";

export interface PostVideoLibraryCardProps {
  record: UserAssetRecord;
  subTab: PostVideoLibrarySubTab;
  selected?: boolean;
  onSelect?: (record: UserAssetRecord) => void;
}

const cardButtonClass = (selected?: boolean) =>
  twMerge(
    "flex w-full shrink-0 cursor-pointer flex-col overflow-hidden rounded-[4px] border text-left transition-colors",
    selected ? "border-2 border-kds-accent" : "border border-kds-border",
  );

const libraryCardAttrs = (record: UserAssetRecord) => ({
  "data-testid": TestId.PostVideoLibraryCard,
  ...(isNotNil(record.id) ? { "data-asset-id": String(record.id) } : {}),
});

export const PostVideoGeneratedCard = ({ record, subTab, selected, onSelect }: PostVideoLibraryCardProps) => {
  const coverUrl = getPostVideoCoverUrl(record, subTab);
  const productImage = getPostVideoProductImage(record);
  const productTitle = getPostVideoProductTitle(record);

  return (
    <button
      type="button"
      {...libraryCardAttrs(record)}
      onClick={() => onSelect?.(record)}
      className={twMerge(cardButtonClass(selected), "bg-kds-bg-card")}
      style={{ width: LIBRARY_CARD_WIDTH, height: GENERATED_CARD_HEIGHT }}
    >
      <div
        className="relative w-full shrink-0 overflow-hidden bg-kds-bg-subtle"
        style={{ height: GENERATED_COVER_HEIGHT }}
      >
        {coverUrl && <img src={coverUrl} alt="" className="pointer-events-none h-full w-full object-cover" />}
      </div>
      <div className="flex min-h-0 flex-1 items-center gap-[8px] p-[12px]">
        <div className="size-[32px] shrink-0 overflow-hidden rounded-[4px] border border-kds-border-soft bg-kds-bg-subtle">
          {productImage && <img src={productImage} alt="" className="pointer-events-none h-full w-full object-cover" />}
        </div>
        <p className="m-0 line-clamp-2 min-h-0 flex-1 text-[12px] leading-[16px] text-kds-fg-soft">{productTitle}</p>
      </div>
    </button>
  );
};

export const PostVideoUploadedCard = ({ record, subTab, selected, onSelect }: PostVideoLibraryCardProps) => {
  const [isPortrait, setIsPortrait] = useState(true);
  const videoUrl = getPostVideoVideoUrl(record);
  const coverUrl = getPostVideoCoverUrl(record, subTab);
  const mediaUrl = videoUrl ?? coverUrl;
  const hasVideo = Boolean(videoUrl);

  return (
    <button
      type="button"
      {...libraryCardAttrs(record)}
      onClick={() => onSelect?.(record)}
      className={twMerge(cardButtonClass(selected), "items-center justify-center bg-kds-dark")}
      style={{ width: LIBRARY_CARD_WIDTH, height: UPLOADED_CARD_HEIGHT }}
    >
      <div
        className="relative flex w-full items-center justify-center overflow-hidden"
        style={{ height: UPLOADED_CARD_MEDIA_HEIGHT }}
      >
        {hasVideo ? (
          <video
            src={videoUrl}
            poster={coverUrl}
            muted
            preload="metadata"
            playsInline
            onLoadedMetadata={(event) => {
              const target = event.currentTarget;
              setIsPortrait(target.videoHeight >= target.videoWidth);
            }}
            className={classNames("pointer-events-none h-full w-full", isPortrait ? "object-cover" : "object-contain")}
          />
        ) : (
          mediaUrl && <img src={mediaUrl} alt="" className="pointer-events-none h-full w-full object-cover" />
        )}
      </div>
    </button>
  );
};

export const PostVideoLibraryCard = ({ record, subTab, selected, onSelect }: PostVideoLibraryCardProps) => {
  if (subTab === "generated") {
    return <PostVideoGeneratedCard record={record} subTab={subTab} selected={selected} onSelect={onSelect} />;
  }
  return <PostVideoUploadedCard record={record} subTab={subTab} selected={selected} onSelect={onSelect} />;
};
